/** The forward-to address's local part: a readable base derived from the
 * organization and workspace names, then a dot and a random token
 * ("toms-landscaping.k7f2q9mx"). Shared by the API (which mints the real
 * handle) and the web app (which previews it during onboarding before the
 * handle exists). One derivation, so the preview never disagrees with the
 * address that is minted a moment later. */

/** The domain every forward-to address is minted on, and the one place the
 * name is written. `email.inboundDomain` in packages/config/src/runtime.ts
 * defaults to it (the environment can still override it), apps/marketing
 * prints it on the pages that show a sample address, and the web app previews
 * an address with it before one exists. */
export const INBOUND_EMAIL_DOMAIN = "in.ledgerbox.io"

const maxHandleLength = 40
const fallbackHandle = "inbox"

// Names and slugs are both user-editable and only unique within their own
// scope, so nothing guarantees either is safe as a local-part.
const sanitizeHandlePart = (value: string): string =>
  value
    .toLowerCase()
    // "Tom's" reads as "toms", not "tom-s".
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")

// The base has to read like "acme-books", and the name is
// what the customer actually typed. The slug only looks friendly when someone
// chose it: the default-org bootstrap mints slugs like
// "org-2wx1ulltlvff-fcb3b929", which would produce exactly the opaque token
// this feature promised not to hand out. So prefer the name and keep the slug
// as the fallback for a name that sanitizes away entirely (an org named in a
// non-Latin script, say). Uniqueness never depended on this: the token and the
// handle's unique index own that.
const preferredHandlePart = (name: string, slug: string): string =>
  sanitizeHandlePart(name) || sanitizeHandlePart(slug)

export const buildInboxHandleBase = (
  organizationLabel: {
    name: string
    slug: string
    /** A firm's client gets the short address, from the client alone. */
    collectsFrom?: "clients" | "own_business" | null
  },
  workspaceLabel: { name: string; slug: string }
): string => {
  const organization = preferredHandlePart(
    organizationLabel.name,
    organizationLabel.slug
  )
  const workspace = preferredHandlePart(
    workspaceLabel.name,
    workspaceLabel.slug
  )
  // "toms-landscaping." rather than "ambers-bookkeeping-toms-landscaping.":
  // a firm forwards to one address per client, and the firm's name only made
  // it long. Uniqueness is the token's job, not the base's.
  if (organizationLabel.collectsFrom === "clients" && workspace) {
    return (
      workspace.slice(0, maxHandleLength).replace(/-+$/, "") || fallbackHandle
    )
  }
  // A bootstrapped account has both parts set to "default", and
  // "default-default.…@in.ledgerbox.io" reads like a bug rather than an address.
  const parts =
    workspace === organization ? [organization] : [organization, workspace]

  const base = parts
    .filter(Boolean)
    .join("-")
    .slice(0, maxHandleLength)
    // Clamping can land mid-word and leave a trailing separator behind.
    .replace(/-+$/, "")

  return base || fallbackHandle
}

export const formatInboxAddress = (
  handle: string,
  inboundDomain: string
): string => `${handle}@${inboundDomain}`

// The token is the credential. Anyone who knows a live address can file mail
// into that client, so the base is only there to help a person recognize the
// address and the token is what nobody can guess: 8 characters of Crockford
// base32 (40 bits) from a CSPRNG. The API mints it with node:crypto; nothing
// in this module generates one, because this module also runs in the browser.
// Lower case, and without i, l, o and u, so the characters people confuse
// with 1 and 0 never appear in an address someone types from a screenshot.
export const INBOX_HANDLE_TOKEN_ALPHABET = "0123456789abcdefghjkmnpqrstvwxyz"

export const INBOX_HANDLE_TOKEN_LENGTH = 8

const inboxHandlePattern = new RegExp(
  `^[a-z0-9-]+\\.[${INBOX_HANDLE_TOKEN_ALPHABET}]{${INBOX_HANDLE_TOKEN_LENGTH}}$`
)

export const buildInboxHandle = (base: string, token: string): string =>
  `${base}.${token}`

/** True for a handle this codebase could have minted. The inbound webhook
 * uses it to drop mail for an old untokenized address without a lookup. */
export const isInboxHandle = (handle: string): boolean =>
  inboxHandlePattern.test(handle)

/** The address before it exists: the readable base and a masked token, so an
 * onboarding screen can show its shape without promising a real address. */
export const formatInboxAddressPreview = (
  base: string,
  inboundDomain: string
): string =>
  `${base}.${"\u2022".repeat(INBOX_HANDLE_TOKEN_LENGTH)}@${inboundDomain}`
